import type { FileUsageBucket } from "./types";

/**
 * Áreas com cor própria na barra de composição, cada uma presa a uma série do gráfico.
 * A cor segue a área, nunca a posição: uma área nova ou um filtro não repinta as outras.
 * As demais entram em "Outros", em cinza.
 */
const AREA_SERIES: Readonly<Record<string, number>> = {
    projects: 1,
    diaries: 2,
    steps: 3,
    offers: 4,
    users: 5,
};

export const OTHERS_KEY = "others";

export interface CompositionSegment {
    /** Rótulo da área (`byArea`) ou `OTHERS_KEY`. */
    key: string;
    bytes: number;
    files: number;
    /** Fatia do total, de 0 a 100. */
    share: number;
    /** Cor CSS da fatia. */
    color: string;
}

const seriesColor = (key: string): string => {
    const series = AREA_SERIES[key];
    return series ? `var(--chart-${series})` : "var(--chart-neutral)";
};

/** Fatias da barra: as áreas com cor própria, na ordem do backend (maior primeiro), e "Outros" no fim. */
export function buildAreaComposition(buckets: readonly FileUsageBucket[], totalBytes: number): CompositionSegment[] {
    if (totalBytes <= 0) return [];
    const share = (bytes: number) => (bytes / totalBytes) * 100;

    const named = buckets.filter((bucket) => bucket.bytes > 0 && AREA_SERIES[bucket.label] !== undefined);
    const rest = buckets.filter((bucket) => bucket.bytes > 0 && AREA_SERIES[bucket.label] === undefined);

    const segments = named.map<CompositionSegment>((bucket) => ({
        key: bucket.label,
        bytes: bucket.bytes,
        files: bucket.files,
        share: share(bucket.bytes),
        color: seriesColor(bucket.label),
    }));
    if (rest.length > 0) {
        const bytes = rest.reduce((sum, bucket) => sum + bucket.bytes, 0);
        const files = rest.reduce((sum, bucket) => sum + bucket.files, 0);
        segments.push({ key: OTHERS_KEY, bytes, files, share: share(bytes), color: "var(--chart-neutral)" });
    }
    return segments;
}

/** Porcentagem para exibir: inteira, e "<1%" em vez de um 0% que esconde uma fatia real. */
export function formatShare(share: number, locale?: string): string {
    if (share > 0 && share < 1) return "<1%";
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(share)}%`;
}
